/**
 * Central de Importação - Sistema > Central de Importação
 * Fluxo: 1) Selecionar entidade, 2) Upload e validação, 3) Importação
 * Layout alinhado ao sistema (msgbox, notify, ajuda ? como cadastro de produto)
 */
import api from '../api.js';
import msgbox, { notify } from '../../versa/msgbox.js';

const IMPORT_HELP_CLASS = 'import-help-body';

const IMPORT_HELP_1 = `
<div class="${IMPORT_HELP_CLASS}">
<h4>Visão geral</h4>
<p>A central carrega da API a lista de tipos de cadastro que podem ser importados por planilha. O fluxo é: <strong>escolher entidade</strong> → <strong>enviar arquivo</strong> → <strong>validar</strong> → <strong>importar</strong> só as linhas aprovadas.</p>
<h4>Organização</h4>
<p>Cadastros ligados à empresa depositante (ex.: fornecedores, clientes, transportadores), conforme habilitado no servidor.</p>
<h4>Catálogo</h4>
<p>Itens de catálogo (ex.: produtos, categorias, unidades), conforme a configuração de importação.</p>
<h4>Armazenamento</h4>
<p>Dados de armazém/estoque quando expostos para importação em massa.</p>
<p><strong>Permissões:</strong> seu grupo de usuário precisa ter os endpoints da central de importação liberados; caso contrário a lista pode vir vazia ou a API retorna erro.</p>
</div>`;

const IMPORT_HELP_2 = `
<div class="${IMPORT_HELP_CLASS}">
<h4>Arquivo</h4>
<p>Formatos aceitos: <strong>CSV</strong> ou <strong>XLSX</strong>. A primeira linha deve conter os cabeçalhos exatamente como no modelo.</p>
<h4>Colunas esperadas</h4>
<p>A tela mostra a lista de colunas obrigatórias/opcionais para a entidade escolhida. Desvios de nome ou ordem podem gerar erro na validação.</p>
<h4>Baixar modelo</h4>
<p>Use o link <strong>Baixar modelo da planilha</strong> para obter um CSV de exemplo com os cabeçalhos corretos.</p>
<h4>Validar</h4>
<p>O botão <strong>Validar</strong> envia o arquivo ao servidor <strong>sem gravar</strong> ainda: você vê erros por linha e quantas linhas estão prontas para importar.</p>
</div>`;

const IMPORT_HELP_3 = `
<div class="${IMPORT_HELP_CLASS}">
<h4>Resultado da validação</h4>
<p>Se houver erros, cada item indica a <strong>linha</strong>, o <strong>campo</strong> (quando aplicável) e a <strong>mensagem</strong>. Corrija o arquivo e volte à etapa anterior para reenviar.</p>
<h4>Linhas válidas</h4>
<p>Somente as linhas sem bloqueio entram em <strong>rows_valid</strong>. O botão <strong>Importar</strong> só aparece quando existe ao menos uma linha válida.</p>
<h4>Voltar</h4>
<p>Retorna à seleção de entidade e descarta o arquivo atual da memória do assistente (não altera o que já foi gravado no banco em importações anteriores).</p>
</div>`;

const IMPORT_HELP_4 = `
<div class="${IMPORT_HELP_CLASS}">
<h4>Importação concluída</h4>
<p>O número exibido é a quantidade de registros gravados com sucesso nesta execução.</p>
<h4>Falhas parciais</h4>
<p>Se algumas linhas falharem no insert, elas aparecem listadas com linha e mensagem. As demais podem ter sido gravadas normalmente.</p>
<h4>Nova importação</h4>
<p>Reinicia o fluxo na primeira tela para outro arquivo ou outra entidade.</p>
</div>`;

const IMPORT_HELP_BY_STEP = {
	1: { title: 'Ajuda — Central de Importação (passo 1)', html: IMPORT_HELP_1 },
	2: { title: 'Ajuda — Envio e validação do arquivo', html: IMPORT_HELP_2 },
	3: { title: 'Ajuda — Resultado da validação', html: IMPORT_HELP_3 },
	4: { title: 'Ajuda — Após importar', html: IMPORT_HELP_4 },
};

function openImportHelp(step) {
	const pack = IMPORT_HELP_BY_STEP[step];
	if (!pack) return;
	let mb;
	mb = msgbox({
		title: pack.title,
		text: pack.html,
		buttons: [{ label: 'Fechar', cls: 'bn_Enter' }],
		callback: () => mb.destroy(),
	});
}

const CATEGORIAS = {
	organização: 'Organização',
	catálogo: 'Catálogo',
	armazenamento: 'Armazenamento',
};

let entities = {};
let selectedEntity = null;
let validationResult = null;

function $(sel, ctx = document) {
	return ctx.querySelector(sel);
}

function renderEntitySelector(container) {
	const groups = {};
	for (const [key, schema] of Object.entries(entities)) {
		const cat = schema.category || 'outros';
		if (!groups[cat]) groups[cat] = [];
		groups[cat].push({ key, ...schema });
	}
	const order = ['organização', 'catálogo', 'armazenamento'];
	let html = '<div class="import-entity-list">';
	for (const cat of order) {
		if (!groups[cat]) continue;
		html += `<div class="import-category mtm">`;
		html += `<div class="import-cat-title">${CATEGORIAS[cat] || cat}</div>`;
		html += '<div class="import-cat-items">';
		for (const e of groups[cat]) {
			html += `<button type="button" data-entity="${e.key}" class="import-entity-btn button">${e.label}</button>`;
		}
		html += '</div></div>';
	}
	html += '</div>';
	container.innerHTML = html;
}

function renderStep1(container) {
	container.innerHTML = `
		<div class="msgbox import-step" data-step="1">
			<div class="import-title-row">
				<div class="title">Central de Importação</div>
				<button type="button" class="import-help-btn" data-import-help="1" title="Ajuda desta etapa" aria-label="Ajuda passo 1">?</button>
			</div>
			<p class="mts">Selecione o tipo de cadastro para importar:</p>
			<div id="import-entity-list"></div>
		</div>`;
	renderEntitySelector($('#import-entity-list', container));
}

function renderStep2(container, entityKey) {
	const schema = entities[entityKey];
	const cols = (schema?.cols || []).join(', ');
	container.innerHTML = `
		<div class="form import-step" data-step="2">
			<div class="import-title-row">
				<div class="title">Importar ${schema?.label || entityKey}</div>
				<button type="button" class="import-help-btn" data-import-help="2" title="Ajuda desta etapa" aria-label="Ajuda passo 2">?</button>
			</div>
			<p class="mts">Colunas esperadas: <strong>${cols}</strong></p>
			<div class="mtl fsb">
				<label>Arquivo (CSV ou XLSX)<sup>*</sup>
					<input id="import-file" type="file" accept=".csv,.xlsx" required>
				</label>
			</div>
			<div class="mtm">
				<a id="import-download-template" href="#" class="link">Baixar modelo da planilha</a>
			</div>
			<div class="mtm fsb import-actions">
				<button type="button" id="import-back" class="button import-btn-muted">Voltar</button>
				<button type="button" id="import-validate" class="button import-btn-success">Validar</button>
			</div>
		</div>`;
}

function renderStep3(container, result) {
	const ok = result?.ok === true;
	const errors = result?.errors || [];
	const rowsValid = result?.rows_valid || [];
	const total = result?.total || 0;

	let errorsHtml = '';
	if (errors.length > 0) {
		errorsHtml = '<div class="import-errors mtm"><strong>Problemas encontrados:</strong><ul>';
		for (const e of errors.slice(0, 20)) {
			errorsHtml += `<li>Linha ${e.linha}: ${e.campo ? e.campo + ' – ' : ''}${e.mensagem}</li>`;
		}
		if (errors.length > 20) {
			errorsHtml += `<li>... e mais ${errors.length - 20} erro(s)</li>`;
		}
		errorsHtml += '</ul></div>';
	}

	let successHtml = '';
	if (ok && rowsValid.length > 0) {
		successHtml = `<div class="import-success mts"><strong>Validação OK.</strong> ${rowsValid.length} de ${total} linha(s) prontas para importar.</div>`;
	} else if (ok && rowsValid.length === 0) {
		successHtml = '<div class="import-warning mts">Nenhuma linha válida encontrada.</div>';
	}

	container.innerHTML = `
		<div class="msgbox import-step" data-step="3">
			<div class="import-title-row">
				<div class="title">Resultado da validação</div>
				<button type="button" class="import-help-btn" data-import-help="3" title="Ajuda desta etapa" aria-label="Ajuda passo 3">?</button>
			</div>
			${successHtml}
			${errorsHtml}
			<div class="mtm fsb import-actions">
				<button type="button" id="import-back2" class="button import-btn-muted">Voltar</button>
				${ok && rowsValid.length > 0 ? '<button type="button" id="import-do" class="button import-btn-success">Importar</button>' : ''}
			</div>
		</div>`;
}

function renderStep4(container, result) {
	const imported = result?.imported || 0;
	const failed = result?.failed || [];
	let failedHtml = '';
	if (failed.length > 0) {
		failedHtml = '<div class="import-errors mtm"><strong>Falhas:</strong><ul>';
		for (const f of failed.slice(0, 10)) {
			failedHtml += `<li>Linha ${f.linha}: ${f.mensagem}</li>`;
		}
		if (failed.length > 10) {
			failedHtml += `<li>... e mais ${failed.length - 10}</li>`;
		}
		failedHtml += '</ul></div>';
	}
	container.innerHTML = `
		<div class="msgbox import-step" data-step="4">
			<div class="import-title-row">
				<div class="title">Importação concluída</div>
				<button type="button" class="import-help-btn" data-import-help="4" title="Ajuda desta etapa" aria-label="Ajuda passo 4">?</button>
			</div>
			<div class="import-success mts"><strong>${imported}</strong> registro(s) importado(s) com sucesso.</div>
			${failedHtml}
			<div class="mtm import-actions">
				<button type="button" id="import-new" class="button import-btn-success">Nova importação</button>
			</div>
		</div>`;
}

async function loadEntities() {
	const r = await api.import_center_entities();
	if (!r.ok) throw new Error('Erro ao carregar entidades');
	entities = await r.json();
}

async function downloadTemplate(entityKey) {
	const r = await api.import_center_template(entityKey);
	if (!r.ok) throw new Error('Erro ao baixar modelo');
	const blob = await r.blob();
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = `modelo_${entityKey}.csv`;
	a.click();
	URL.revokeObjectURL(a.href);
}

async function validate(entityKey, file) {
	const fd = new FormData();
	fd.append('entity_key', entityKey);
	fd.append('file', file);
	const r = await api.import_center_validate(fd);
	if (!r.ok) {
		const text = await r.text();
		throw new Error(text || 'Erro na validação');
	}
	return r.json();
}

/** Lotes no POST /import — evita 502 (body JSON grande derruba uWSGI sem post-buffering). */
const IMPORT_BATCH_SIZE = 150;

async function doImport(entityKey, rowsValid) {
	const rows = Array.isArray(rowsValid) ? rowsValid : [];
	if (rows.length === 0) {
		return { imported: 0, failed: [], total: 0 };
	}
	let imported = 0;
	let failed = [];
	for (let i = 0; i < rows.length; i += IMPORT_BATCH_SIZE) {
		const chunk = rows.slice(i, i + IMPORT_BATCH_SIZE);
		const r = await api.import_center_import({ entity_key: entityKey, rows_valid: chunk });
		if (!r.ok) {
			const text = await r.text();
			throw new Error(text || `Erro na importação (lote ${Math.floor(i / IMPORT_BATCH_SIZE) + 1})`);
		}
		const part = await r.json();
		imported += Number(part?.imported || 0);
		if (Array.isArray(part?.failed) && part.failed.length) {
			failed = failed.concat(part.failed);
		}
	}
	return { imported, failed, total: rows.length };
}

function showError(msg) {
	notify(String(msg || 'Erro'), 'Central de Importação');
}

const import_center = async function() {
	const workarea = document.getElementById('workarea');
	if (!workarea) return;

	const styles = `
		#import-center-wrap {
			display: flex;
			justify-content: center;
			align-items: flex-start;
			padding: var(--space-lg);
			min-height: 100%;
			background-color: var(--color-secondary);
		}
		#import-center { max-width: 42rem; width: 100%; }
		#import-center .import-title-row {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: 0.75rem;
			flex-wrap: wrap;
		}
		#import-center .import-title-row .title { margin: 0; flex: 1; min-width: 0; }
		#import-center .import-help-btn {
			display: inline-flex;
			align-items: center;
			justify-content: center;
			width: 1.35rem;
			height: 1.35rem;
			padding: 0;
			border: 1px solid #9ca3af;
			border-radius: 999px;
			background: #f3f4f6;
			color: #374151;
			font-size: 0.85rem;
			font-weight: 700;
			line-height: 1;
			cursor: pointer;
			flex-shrink: 0;
		}
		#import-center .import-help-btn:hover { background: #e5e7eb; }
		#import-center .import-help-btn:focus { outline: 2px solid #2563eb; outline-offset: 2px; }
		#import-center .import-actions { justify-content: flex-end; gap: 0.5rem; flex-wrap: wrap; }
		#import-center .import-btn-success {
			background: linear-gradient(180deg, #4ade80 0%, #22c55e 100%);
			color: #fff;
			border: 1px solid #15803d;
			font-weight: 600;
			box-shadow: 0 1px 2px rgba(22, 163, 74, 0.25);
		}
		#import-center .import-btn-success:hover {
			filter: brightness(1.05);
			box-shadow: 0 2px 4px rgba(22, 163, 74, 0.3);
		}
		#import-center .import-btn-muted {
			background: #f8fafc;
			color: #334155;
			border: 1px solid var(--color-border);
			font-weight: 500;
		}
		#import-center .import-btn-muted:hover { background: #f1f5f9; }
		#import-center .import-cat-title {
			font-weight: 600;
			margin-bottom: var(--space-sm);
			color: var(--color-text);
		}
		#import-center .import-cat-items {
			display: flex;
			flex-wrap: wrap;
			gap: var(--space-sm);
		}
		#import-center .import-entity-btn {
			display: inline-block;
			text-align: center;
			margin: 0;
			text-decoration: none;
		}
		#import-center .import-errors {
			color: var(--color-error);
			padding: var(--space-sm);
			background-color: var(--color-secondary);
			border: 1px solid var(--color-border);
			border-radius: 4px;
		}
		#import-center .import-errors ul { margin: var(--space-sm) 0 0; padding-left: var(--space-lg); }
		#import-center .import-success {
			color: #0a6b0a;
			padding: var(--space-sm);
			background-color: #f0f9f0;
			border: 1px solid #c8e6c8;
			border-radius: 4px;
		}
		#import-center .import-warning {
			color: #8a6d00;
			padding: var(--space-sm);
			background-color: #fffbe6;
			border: 1px solid #e6d68a;
			border-radius: 4px;
		}
	`;

	const helpBodyStyles = `
		.import-help-body { max-height: 65vh; overflow-y: auto; text-align: left; }
		.import-help-body h4 {
			margin: 0.85rem 0 0.25rem 0;
			font-size: 0.78rem;
			font-weight: 700;
			text-transform: uppercase;
			letter-spacing: 0.03em;
			color: #1f2937;
		}
		.import-help-body h4:first-child { margin-top: 0; }
		.import-help-body p {
			margin: 0;
			font-size: 0.875rem;
			line-height: 1.45;
			color: #374151;
		}
	`;

	workarea.innerHTML = `<style>${styles}</style><style>${helpBodyStyles}</style><div id="import-center-wrap"><div id="import-center"></div></div>`;
	const container = $('#import-center');

	try {
		await loadEntities();
	} catch (e) {
		showError(e.message);
		return;
	}

	container.addEventListener('click', async (ev) => {
		const helpBtn = ev.target.closest('.import-help-btn');
		if (helpBtn) {
			ev.preventDefault();
			const step = Number(helpBtn.getAttribute('data-import-help') || '0');
			if (step >= 1 && step <= 4) openImportHelp(step);
			return;
		}

		const entityBtn = ev.target.closest('.import-entity-btn');
		if (entityBtn) {
			ev.preventDefault();
			selectedEntity = entityBtn.dataset.entity;
			renderStep2(container, selectedEntity);
			$('#import-file').value = '';
			return;
		}

		if (ev.target.id === 'import-download-template') {
			ev.preventDefault();
			try {
				await downloadTemplate(selectedEntity);
			} catch (e) {
				showError(e.message);
			}
			return;
		}

		if (ev.target.id === 'import-back' || ev.target.id === 'import-back2') {
			renderStep1(container);
			selectedEntity = null;
			validationResult = null;
			return;
		}

		if (ev.target.id === 'import-validate') {
			const fileInput = $('#import-file');
			const file = fileInput?.files?.[0];
			if (!file) {
				showError('Selecione um arquivo.');
				return;
			}
			try {
				validationResult = await validate(selectedEntity, file);
				renderStep3(container, validationResult);
			} catch (e) {
				showError(e.message);
			}
			return;
		}

		if (ev.target.id === 'import-do') {
			const rows = validationResult?.rows_valid || [];
			if (rows.length === 0) return;
			try {
				const result = await doImport(selectedEntity, rows);
				renderStep4(container, result);
			} catch (e) {
				showError(e.message);
			}
			return;
		}

		if (ev.target.id === 'import-new') {
			renderStep1(container);
			selectedEntity = null;
			validationResult = null;
			return;
		}
	});

	renderStep1(container);
};

export default import_center;
